<template>
  <div class="transaction-tab">
    <v-progress-linear class="loading" v-show="loading" :indeterminate="true"></v-progress-linear>
    <search-input :search="search" placeholder="Search" @on-search="handleSearch"></search-input>
    <div v-show="!loading && count !== undefined" class="total-ctn">Total: {{this.count}}</div>
    <list v-show="!loading" ref="box">
      <list-item v-for="transaction in filteredTransactions" @handle-click="selectTransaction(transaction.id)" :key="transaction.message" :selected="detailedTransaction.id == transaction.id" slot="item">
        <transaction-item :type="type" :transaction="transaction"></transaction-item>
      </list-item>
      <info-card v-show="detailedTransaction.id" slot="info-card" :style="{'margin-top': detailsMarginTopPx + 'px'}" ref="infoCard">
        <transaction-card :type="type" :transaction="detailedTransaction" @edit="openEditCodeDialog(detailedTransaction, $event)"></transaction-card>
      </info-card>
    </list>
    <v-btn
      v-scroll="onScroll"
      v-show="showScrollHelper" fab
      dark fixed bottom
      right color="primary"
      @click="scrollToInfoCard">
      <v-icon>{{scrollHelperUp ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}}</v-icon>
    </v-btn>
    <edit-code-dialog
      :dialog="editTransactionDialog"
      :transaction="editedTransaction"
      @edit="editTransaction"
      @cancel="editTransactionDialog = false" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getAbsoluteElementHeight } from '@/utils/common'

import List from '@/components/List'
import ListItem from '@/components/ListItem'
import InfoCard from '@/components/InfoCard'

import TransactionItem from '@/components/TransactionItem'
import TransactionCard from '@/components/TransactionCard'
import EditCodeDialog from '@/components/EditCodeDialog'

import SearchInput from '@/components/SearchInput'

const listItemHeight = 72

export default {
  name: 'TransactionsTab',
  components: {
    'list': List,
    'list-item': ListItem,
    'info-card': InfoCard,
    'transaction-item': TransactionItem,
    'transaction-card': TransactionCard,
    'edit-code-dialog': EditCodeDialog,
    'search-input': SearchInput
  },
  data () {
    return {
      detailsMarginTopPx: 0,
      showScrollHelper: false,
      scrollHelperUp: true,

      editTransactionDialog: false,
      editedTransaction: null
    }
  },
  props: {
    type: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters(['profile']),
    ...mapGetters('codes', [
      'search',
      'loading',
    ]),
    ...mapGetters('transactions', [
      'selectedTransactionId',
      'transactions',
    ]),
    // filteredTransactions should be moved to vuex as parametrized getter for better code if have time
    // same for count then (filteredTransactionsCount getter)
    filteredTransactions() {
      if (this.type == 'PENDING_TRANSACTIONS') {
        if (this.profile.role == 3)
          return this.transactions.filter(txn => txn.status == 1)
        else
          return this.transactions.filter(txn => txn.status != 4)
      }
      if (this.type == 'COMPLETED_TRANSACTIONS')
        return this.transactions.filter(txn => txn.status == 4)
      return []
    },
    count() {
      return this.filteredTransactions.length || 0
    },
    detailedTransaction() {
      return this.filteredTransactions.find(transaction => transaction.id == this.selectedTransactionId) || { code: {} }
    },
    detailedTransactionPosition() {
      return this.filteredTransactions.findIndex(transaction => transaction.id == this.selectedTransactionId)
    },
    detailsHeight() {
      return this.detailedTransaction && this.$refs.infoCard.$el.clientHeight || 774
    }
  },
  watch: {
    detailedTransactionPosition: {
      immediate: true,
      handler() {
        this.$nextTick(() => this.detailsMarginTopPx = this.computeDetailsMargin())
      }
    },
  },
  methods: {
    onScroll () {
      if (typeof window === 'undefined') return
      const viewportTopOffsetOfDetails = this.detailsMarginTopPx + 130
      const isViewportAboveDetails = window.pageYOffset + window.innerHeight < viewportTopOffsetOfDetails + 200
      const isViewportBelowDetails = viewportTopOffsetOfDetails < window.pageYOffset
      this.showScrollHelper = isViewportAboveDetails || isViewportBelowDetails
      this.scrollHelperUp = isViewportBelowDetails
    },
    selectTransaction (transactionId) {
      this.$store.dispatch('transactions/selectTransaction', { transactionId })
    },
    openEditCodeDialog (transaction, scmData) {
      this.editedTransaction = transaction
      this.editedTransaction.scmData = scmData
      this.editTransactionDialog = true
    },
    async editTransaction ({extendedId, update}) {
      await this.$store.dispatch('transactions/createTransaction', {extendedId, update})
      this.editTransactionDialog = false
    },
    handleSearch (search) {
      this.$store.dispatch('transactions/clearTransactionSelection')
      this.$store.dispatch('transactions/searchTransaction', search)
    },
    scrollToInfoCard () {
      window.scrollTo({
        top: this.detailsMarginTopPx + 130,
        left: 0,
        behavior: 'smooth'
      })
    },
    computeDetailsMargin () {
      // list item pointer is in first half of details card, no margin for the card
      if (this.detailedTransactionPosition * listItemHeight < this.detailsHeight / 2)
        return 0
      const bottomOffsetIndexes = this.count - this.detailedTransactionPosition
      const boxHeight = this.count * listItemHeight
      let res
      if (bottomOffsetIndexes <= Math.floor(this.detailsHeight / 2 / listItemHeight)) {
        // info card has reached end of the list on the left, aligning it with the list bottom
        // moving accordingly with list item pointer being in the center of info card
        res = boxHeight - this.detailsHeight
      } else {
        res = this.detailedTransactionPosition * listItemHeight - this.detailsHeight / 2
        res -= res % listItemHeight + 200
      }
      return Math.max(0, res)
    }
  },
  async created () {
    if (!this.search) {
      await this.$store.dispatch('scmFields/loadSCMFieldList')
      await this.$store.dispatch('transactions/loadTransactionList')
    }
  }
}

</script>

<style lang="scss" scoped>
.transaction-tab{
  position: relative;
  padding: 0px;
  background: white;
}
</style>
